import {
  addToast,
  clearAllToasts,
  ErrorToast,
  Toastß,
} from '@otter-ai/den-components';
import { GreenCheckIcon } from '@otter-ai/den-icons';
import { cloneTemplate, deleteTemplate } from '@otter-ai/recall-client';
import {
  CloneTemplateEvent,
  DeleteTemplateEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { templatesQueryCreator } from '../query/templatesQueryCreator';

type TemplateType = 'personal' | 'admin';

export const useCloneTemplate = (
  type: TemplateType,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
    }: {
      templateId: number;
      templateName: string;
    }) => {
      const response = await cloneTemplate({
        body: {
          template_id: templateId,
        },
      });
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: templatesQueryCreator().queryKey,
      });
      const { templateId, templateName } = variables;
      trackEvent(
        CloneTemplateEvent({
          TemplateType: type,
          TemplateId: templateId,
          TemplateName: templateName,
        }),
      );
      clearAllToasts();
      addToast(
        <Toastß
          description="Template cloned."
          altText="Template cloned."
          icon={<GreenCheckIcon size={24} />}
          descriptionContainerClassName="max-w-[220px] px-4"
          showCloseIcon
        />,
        { duration: 5_000 },
      );
      onSuccess?.();
    },
    onError: (error) => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={`Failed to clone template, ${error.message}`}
        />,
        { duration: 5_000 },
      );
    },
  });
};

export const useDeleteTemplate = (
  type: TemplateType,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
    }: {
      templateId: number;
      templateName: string;
    }) => {
      const response = await deleteTemplate({
        body: {
          template_id: templateId,
        },
      });
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: templatesQueryCreator().queryKey,
      });
      const { templateId, templateName } = variables;
      trackEvent(
        DeleteTemplateEvent({
          TemplateType: type,
          TemplateId: templateId,
          TemplateName: templateName,
        }),
      );
      clearAllToasts();
      addToast(
        <Toastß
          description="Template deleted."
          altText="Template deleted."
          icon={<GreenCheckIcon size={24} />}
          descriptionContainerClassName="max-w-[220px] px-4"
          showCloseIcon
        />,
        { duration: 5_000 },
      );
      onSuccess?.();
    },
    onError: (error) => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={`Failed to delete template, ${error.message}`}
        />,
        { duration: 5_000 },
      );
    },
  });
};
